import React, { useEffect, useRef, useState } from 'react';
import { OPENNESS_OPACITY } from '../fabric/fabricLight';
import { coverTransformQuad } from '../fabric/RoomMockup';
import { shadeQuad } from '../fabric/roomScenes';
import { ProjectedShade } from '../fabric/ProjectedShade';
import { resolveImageUrl } from '../../lib/imageCache';

export const absUrl = (u) => (!u ? null : u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

/* Front-facing window: every quad [TL,TR,BR,BL] is a near axis-aligned rectangle (tolerance in image %) */
const TOL = 1.5;
export const isFrontal = (polys) => polys.every(([tl, tr, br, bl]) =>
  Math.abs(tl[1] - tr[1]) <= TOL && Math.abs(bl[1] - br[1]) <= TOL &&
  Math.abs(tl[0] - bl[0]) <= TOL && Math.abs(tr[0] - br[0]) <= TOL);

/* Openness compositing only on front-facing windows; angled/corner AI scenes are shown as the pure AI image */
export const canComposite = (regions, light) => light?.mode === 'screen' && !!regions?.clean_url && regions?.polygons?.length > 0 && isFrontal(regions.polygons);

/* Same fabric-opacity rule as ConfiguratorShadePreview/RoomMockup: screen → openness table (default 3%), filtering → preset */
export const fabricOpacity = (light, openness) => {
  if (light?.mode === 'screen') {
    const sel = parseInt(openness, 10);
    const pct = OPENNESS_OPACITY[sel] && (!light.options || light.options.includes(sel)) ? sel : (light.options?.includes(3) ? 3 : (light.options?.[0] || 3));
    return { pct, opacity: OPENNESS_OPACITY[pct] ?? 0.95 };
  }
  return { pct: null, opacity: light?.opacity || 0.85 };
};

/* Panels to draw — every detected window gets a shade.
   Prefers the raw `polygons` array (actual detected shade boundaries matching window glass)
   over `panels[].quad` / `panels[].shade_fit` which can drift to image edges after post-processing.
   Falls back to panels for occlusion handling when polygons aren't available.
   Returns [{quad, drop}] in image-% coords. */
export const displayPanels = (regions) => {
  const polys = regions?.polygons || [];
  const panels = regions?.panels;

  // Raw polygons are the most reliable — they match detected shade boundaries within window glass
  if (polys.length) return polys.map(quad => ({ quad, drop: 1 }));

  // Fallback: use panels with occlusion logic
  if (!panels?.length) return [];
  const sorted = panels.map((p, i) => ({ ...p, i })).sort((a, b) => a.quad[0][0] - b.quad[0][0]);
  return sorted.map(p => {
    if (!p.occluded) return { quad: p.quad, drop: 1 };
    const win = p.window || p.quad;
    const top = (win[0][1] + win[1][1]) / 2, bottom = (win[2][1] + win[3][1]) / 2;
    const clear = p.clear_bottom_y != null ? (p.clear_bottom_y - top) / Math.max(1, bottom - top) - 0.03 : 0.6;
    const drop = Math.max(0.25, Math.min(0.6, clear));
    return { quad: shadeQuad(win, drop), drop };
  });
};

/* Openness for AI Scene renders — compositing stack applied inside detected shade panels only.
   The scene <img> underneath is the clean city background (shades removed), so uncovered glass
   shows the city; inside each quad the real swatch is drawn via ProjectedShade at the calibrated
   transmission, revealing the city view progressively (1% ≈ opaque … 10% clearly visible).
   REQUIRES both clean_url (clean background) AND polygons (shade locations) — without both,
   the component renders nothing (no partial overlays that could misalign with AI shade positions). */
export const AiSceneOpenness = ({ regions, imgAspect, containerAspect, color, light, openness, show }) => {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const active = show && canComposite(regions, light) && !!imgAspect;
  const { pct, opacity } = fabricOpacity(light, openness);

  useEffect(() => {
    const el = ref.current;
    if (!el || !active) return undefined;
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [active]);

  if (!active) return null;
  const textureUrl = resolveImageUrl(color?.image_url, 512);
  const hexColor = color?.hex_color || color?.hex || null;

  return (
    <div ref={ref} className="absolute inset-0 pointer-events-none"
      data-testid="ai-openness-layer" data-openness={pct ?? ''} data-opacity={opacity}>
      {size.w > 0 && displayPanels(regions).map(({ quad }, i) => (
        <ProjectedShade
          key={i}
          quad={containerAspect ? coverTransformQuad(quad, imgAspect, containerAspect) : quad}
          cw={size.w} ch={size.h}
          textureUrl={textureUrl}
          hexColor={hexColor}
          opacity={opacity}
          mode={light?.mode === 'screen' ? 'screen' : 'filtering'}
          ambience="day"
          testId={`ai-shade-${i}`}
        />
      ))}
    </div>
  );
};

/* Extract the file path portion of a URL for comparison (strips protocol, host, query, hash) */
const urlPath = (u) => {
  if (!u) return '';
  try { return new URL(u, 'http://x').pathname; } catch { return u; }
};

/* Find the shade regions stored for the render currently displayed */
export const regionsForRender = (color, roomKey, imageUrl) => {
  const regions = color?.shade_regions || {};
  if (roomKey && regions[roomKey]?.polygons) return regions[roomKey];
  const viz = color?.room_visualizations || {};
  // Try exact endsWith first, then pathname-based matching (handles CDN vs origin domain differences)
  const imgPath = urlPath(imageUrl);
  const key = Object.keys(viz).find(k => {
    if (!imageUrl || !viz[k]) return false;
    if (imageUrl.endsWith(viz[k])) return true;
    return imgPath && imgPath === urlPath(viz[k]);
  });
  if (key && regions[key]) return regions[key];
  // Only fall back to any region when we don't have a specific image to match against.
  // When imageUrl is provided but didn't match any visualization URL's regions,
  // the image was likely regenerated and the old regions would be stale.
  if (!imageUrl) {
    return Object.values(regions).find(r => r?.clean_url && r.polygons?.length) || null;
  }
  return null;
};

export default AiSceneOpenness;
